<template>
  <div>
    <v-container>
      <v-btn @click="back" text class="text_btn">
        <v-icon>keyboard_arrow_left</v-icon>back
      </v-btn>
      <h1>Authentication settings ({{ p_name }})</h1>
      <br />
      <v-layout row wrap>
        <v-flex sm6 xs12 md6>
          <v-card>
            <v-card-title>
              <h3>General</h3>
            </v-card-title>
            <v-card-text>
              <p style="margin-bottom:37px;">You can change here some general settings for the authentication module.</p>
              <p>This disables the ability for new users to sign up in your project. This is useful, when you want to manage a list of users by your own.</p>
              <v-switch v-model="general_registrations_disabled" @change="onDisableRegistrationChange" label="Disable new registrations"></v-switch>
              <br>
              <p>Enable the 2FA (Two factor authentication) with an one time password (OTP) for your users.</p>
              <v-switch v-model="otp_allowed" @change="onOTPAllowChange" label="Allow OTP setup"></v-switch>
              <br>
              <p>You can define here additional info, when the user signs in. It will be sent as well with the automatic generated session token.</p>
              <v-text-field v-model="additional_login_info" label="Additional login info"></v-text-field>
              <v-btn @click="saveGeneralInformation" text><v-icon>save</v-icon> save</v-btn>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex sm6 xs12 md6>
          <v-card>
            <v-card-title>
              <h3>Email setup</h3>
            </v-card-title>
            <v-card-text>
              <p>Define here the email settings, in order to send for the users a welcome email or the password reset mail.</p>
              <p>
                <b>Important:</b> If you want to change the settings, please also enter the password again.
              </p>
              <v-form @submit="saveConfiguration">
                <v-layout wrap row>
                  <v-flex xs12 sm6 md6 p-lg3>
                    <v-text-field
                      v-model="email_name"
                      placeholder="ex. John Adam"
                      label="Name of the sender"
                    />
                  </v-flex>
                  <v-flex xs12 sm6 md6 p-lg3>
                    <v-text-field
                      v-model="email_sender"
                      placeholder="ex. john@adam.com"
                      label="Email of the sender"
                    />
                  </v-flex>
                </v-layout>
                <v-layout wrap row>
                  <v-flex xs12 sm6 md6 p-lg3>
                    <v-text-field
                      v-model="email_host"
                      placeholder="ex. mail.server.com"
                      label="Hostname"
                    />
                  </v-flex>
                  <v-flex xs12 sm6 md6 p-lg3>
                    <v-text-field
                      v-model="email_username"
                      placeholder="ex. email_owner"
                      label="Username"
                    />
                  </v-flex>
                  <v-flex xs12 sm6 md6 p-lg3>
                    <v-text-field
                      v-model="email_password"
                      type="password"
                      :placeholder="email_password_hint"
                      label="Password"
                    />
                  </v-flex>
                  <v-flex xs12 sm6 md6 p-lg3>
                    <v-text-field v-model="email_port" placeholder="ex. 587" label="Port" />
                  </v-flex>
                </v-layout>
                <v-btn type="submit" text class="text_btn">
                  <v-icon>save</v-icon>save
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
      <v-layout wrap row>
        <v-flex sm6 xs12>
          <v-card>
            <v-card-title>
              <h3>E-Mail Links</h3>
            </v-card-title>
            <v-card-text>
              <p>Define here all the links, which should be used for the email. Provide them as showed in the example and so not add additional parameters.</p>
              <v-form @submit="saveLinks">
                <v-layout wrap row>
                  <v-flex xs12 sm6 md6 p-lg6>
                    <v-text-field
                      v-model="link_resetmail"
                      placeholder="ex. https://www.domain.com/reset"
                      label="Reset link"
                    />
                    <p>
                      The reset email link is the location, where you call the function to check if the delivered token is correct and offer the user to set a new password.
                      <br />For the generated link in the email, please use the placeholder:
                      <CopyField value="[[RESET_LINK]]" />
                    </p>
                  </v-flex>
                  <v-flex xs12 sm6 md6 p-lg6>
                    <v-text-field
                      v-model="link_activatemail"
                      placeholder="ex. https://www.domain.com/activate"
                      label="Activate link"
                    />
                    <p>
                      The activate email is relevant for the customers, who are using the simple email password provider to authenticate. It will call a special function, if the correct token is provided.
                      <br />For the generated link in the email, please use the placeholder:
                      <CopyField value="[[ACTIVATE_LINK]]" />
                    </p>
                  </v-flex>
                </v-layout>
                <v-btn type="submit" text class="text_btn">
                  <v-icon>save</v-icon>save
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex sm6 xs12>
          <v-card>
            <v-card-title>
              <h3>Email templates</h3>
            </v-card-title>
            <v-card-text>
              <p>Predefine the templates for the emails, which your clients receive, when they creating an account or requesting to reset the password.</p>
              <v-layout>
                <v-flex xs12 sm6 md6>
                  <v-select
                    @change="onEmailTemplateChange"
                    :items="email_templates"
                    v-model="email_template"
                    label="Select template"
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 md6>
                  <v-select
                    @change="onEmailTemplateChange"
                    :items="email_languages"
                    v-model="email_language"
                    label="Select language"
                  ></v-select>
                </v-flex>
              </v-layout>
              <div
                style="margin-top:40px;"
                v-show="this.email_template !== '' && this.email_language !== ''"
              >
                <v-text-field
                  v-model="current_subject"
                  placeholder="ex. Hello user..."
                  label="Subject"
                />
                <wysiwyg v-model="current_content" />
                <br />
                <v-btn @click="saveTemplate" text class="text_btn">
                  <v-icon>save</v-icon>save
                </v-btn>
              </div>
              <div
                style="margin-top:40px;text-align:center"
                v-show="!(this.email_template !== '' && this.email_language !== '')"
              >
                <p>Please select first a template and a language.</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>
<script>
import { AuthSettings } from "../logic/AuthSettings";
import { Projects } from "../logic/Projects";
import { Notifications } from "../notification";

import CopyField from "../components/general/CopyField";

export default {
  name: "AuthenticationSettings",
  data() {
    return {
      p_name: "...",
      project_id: 0,

      //Email setup
      email_name: "",
      email_sender: "",
      email_host: "",
      email_username: "",
      email_password: "",
      email_password_hint: "Not set",
      email_port: "",

      //Email links
      link_resetmail: "",
      link_activatemail: "",

      //Email templates
      email_template: "",
      email_language: "",

      //Select statements
      email_templates: [
        {
          value: "welcome",
          text: "Welcome email"
        },
        {
          value: "reset_password",
          text: "Reset password"
        }
      ],
      email_languages: [
        {
          value: "de",
          text: "German (DE)"
        },
        {
          value: "en",
          text: "English (EN)"
        },
        {
          value: "fr",
          text: "French (FR)"
        },
        {
          value: "it",
          text: "Italian (IT)"
        },
        {
          value: "es",
          text: "Spanish (ES)"
        }
      ],

      current_subject: "",
      current_content: "",

      general_registrations_disabled: false,
      otp_allowed: false,
      additional_login_info: ""
    };
  },
  mounted() {
    this.project_id = this.$route.params.project_id;
    this.loadProjectMeta(this.project_id);
    this.setTitle("Authentication Settings");
    this.loadConfiguration(this.project_id);
  },
  methods: {
    async loadProjectMeta(project_id) {
      var project_response = await Projects.get(project_id);
      if (project_response.status === 200) {
        if (Array.isArray(project_response.body) && project_response.body.length > 0) {
          this.p_name = project_response.body[0].name;
        } else if (project_response.body && project_response.body.name) {
          this.p_name = project_response.body.name;
        }
      }
    },
    async loadConfiguration(project_id) {
      //Step 1: Load the general settings
      var response_disable_registration = await AuthSettings.getDisabledNewRegistrations(project_id);
      if (response_disable_registration.status === 200) {
        var data_disable_registration = response_disable_registration.body;
        this.general_registrations_disabled = data_disable_registration.registration_disabled;
      }
      var response_additional_login_info = await AuthSettings.getAdditionalLoginInfo(project_id);
      if (response_additional_login_info.status === 200) {
        var data_additional_login_info = response_additional_login_info.body;
        this.additional_login_info = data_additional_login_info.additional_login_info;
      }
      var response_otp_allowed = await AuthSettings.getOTPSetupAllowed(project_id);
      if (response_otp_allowed.status === 200) {
        var data_otp_allowed = response_otp_allowed.body;
        this.otp_allowed = data_otp_allowed.otp_allowed;
      }

      //Step 2: Load the credentials for the mail server
      var response = await AuthSettings.load(project_id);

      if (response.status === 200) {
        var data = response.body;
        this.email_name = data.name;
        this.email_sender = data.sender;
        this.email_host = data.host;
        this.email_username = data.username;
        this.email_password_hint = "(Already set)";
        this.email_port = data.port;
      } else if (response.status === 404) {
        //Nothing is happening due, there is no configuration available at that time
      } else {
        this.$router.replace("/project/" + project_id);
        Notifications.show(response.message);
      }

      //Step 3: Load the mail links
      var response2 = await AuthSettings.getMailLinks(project_id);
      if (response2.status === 200) {
        var data2 = response2.body;
        this.link_resetmail = data2.link_reset;
        this.link_activatemail = data2.link_activate;
      }
    },
    async saveConfiguration(e) {
      e.preventDefault();
      var response = await AuthSettings.update(
        this.project_id,
        this.email_name,
        this.email_sender,
        this.email_host,
        this.email_username,
        this.email_password,
        this.email_port
      );
      Notifications.show(response.message);
    },
    onEmailTemplateChange() {
      if (this.email_template !== "" && this.email_language !== "") {
        this.loadEmailTemplate();
      }
    },
    async loadEmailTemplate() {
      var response = await AuthSettings.getMailTemplate(
        this.project_id,
        this.email_template,
        this.email_language
      );

      if (response.status === 200) {
        this.current_subject = response.body.subject;
        this.current_content = this.convertToHtml(response.body.content);
      } else {
        this.current_subject = "";
        this.current_content = "";
      }
    },
    saveTemplate() {
      AuthSettings.saveMailTemplate(
        this.project_id,
        this.email_template,
        this.email_language,
        this.current_subject,
        this.current_content
      ).then(function(response) {
        Notifications.show(response.message);
      });
    },
    saveLinks(e) {
      e.preventDefault();
      AuthSettings.saveMailLinks(
        this.project_id,
        this.link_resetmail,
        this.link_activatemail
      ).then(function(response) {
        Notifications.show(response.message);
      });
    },
    async saveGeneralInformation() {
      var response = await AuthSettings.saveAdditionalLoginInfo(this.project_id, this.additional_login_info);
      if (response.status === 200) {
        Notifications.show("The settings has been saved.");
      } else {
        Notifications.show(response.message);
      }
    },
    async onDisableRegistrationChange() {
      var response = await AuthSettings.toggleDisableRegistration(this.project_id);
      Notifications.show(response.message);
    },
    async onOTPAllowChange() {
      var response;
      if (this.otp_allowed) {
        response = await AuthSettings.enableOTPSetup(this.project_id);
      } else {
        response = await AuthSettings.disableOTPSetup(this.project_id);
      }
      
      Notifications.show(response.message);
    },
    convertToHtml(str) {
      return String(str)
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">");
    },
    back() {
      this.$router.replace("/project/" + this.project_id);
    }
  },
  components: {
    CopyField
  }
};
</script>
<style scoped>
.layout .flex {
  padding-right: 10px;
}
.description {
  padding-top: 10px;
}
.v-card {
  margin-top: 5px;
}
</style>
